import { Injectable } from '@visiba/instantiation';

@Injectable()
export class PlatformService {
	private cachedUserAgentData: Nullable<UserAgentData> = null;

	public static get runtimeIsInBrowser(): boolean {
		return typeof navigator !== 'undefined' && typeof window !== 'undefined';
	}

	public static get runtimeSupportsTouch(): boolean {
		return 'ontouchstart' in window || window.navigator.maxTouchPoints > 0;
	}

	public get runtimeIsInBrowser(): boolean {
		return PlatformService.runtimeIsInBrowser;
	}

	public get runtimeSupportsTouch(): boolean {
		return PlatformService.runtimeSupportsTouch;
	}

	constructor() {
		// Empty
	}

	public get userAgentData(): Readonly<UserAgentData> {
		return this.parseAgentData();
	}

	public browserIs(test: number): boolean {
		const statements: boolean[] = [];
		const agent = this.parseAgentData();

		if ((test & BrowserList.Edge) === BrowserList.Edge) {
			statements.push(agent.browsers.edge);
		}

		if ((test & BrowserList.SamsungBrowser) === BrowserList.SamsungBrowser) {
			statements.push(agent.browsers.samsungBrowser);
		}

		if ((test & BrowserList.Opera) === BrowserList.Opera) {
			statements.push(agent.browsers.opera);
		}

		if ((test & BrowserList.Firefox) === BrowserList.Firefox) {
			statements.push(agent.browsers.firefox);
		}

		if ((test & BrowserList.Chrome) === BrowserList.Chrome) {
			statements.push(agent.browsers.chrome);
		}

		if ((test & BrowserList.Safari) === BrowserList.Safari) {
			statements.push(agent.browsers.safari);
		}

		if ((test & BrowserList.StrictSafari) === BrowserList.StrictSafari) {
			statements.push(agent.browsers.strictSafari);
		}

		if ((test & BrowserList.IE) === BrowserList.IE) {
			statements.push(agent.browsers.ie);
		}

		if ((test & BrowserList.EdgeChromium) === BrowserList.EdgeChromium) {
			statements.push(agent.browsers.edgeChromium);
		}

		if ((test & BrowserList.EdgeAndroid) === BrowserList.EdgeAndroid) {
			statements.push(agent.browsers.edgeAndroid);
		}

		if ((test & BrowserList.EdgeIos) === BrowserList.EdgeIos) {
			statements.push(agent.browsers.edgeIos);
		}

		if ((test & BrowserList.FirefoxIos) === BrowserList.FirefoxIos) {
			statements.push(agent.browsers.firefoxIos);
		}

		if ((test & BrowserList.ChromeIos) === BrowserList.ChromeIos) {
			statements.push(agent.browsers.chromeIos);
		}

		return statements.filter(Boolean).length > 0;
	}

	public platformIs(test: number): boolean {
		const statements: boolean[] = [];
		const agent = this.parseAgentData();

		if ((test & PlatformList.Ios) === PlatformList.Ios) {
			statements.push(agent.platform.ios);
		}

		if ((test & PlatformList.Android) === PlatformList.Android) {
			statements.push(agent.platform.android);
		}

		if ((test & PlatformList.Mobile) === PlatformList.Mobile) {
			statements.push(agent.platform.mobile);
		}

		if ((test & PlatformList.Desktop) === PlatformList.Desktop) {
			statements.push(agent.platform.desktop);
		}

		return statements.filter(Boolean).length > 0;
	}

	public parseAgentData(): Readonly<UserAgentData> {
		if (this.cachedUserAgentData != null) return this.cachedUserAgentData;

		const edgeHTML = this.runtimeIsInBrowser && navigator.userAgent.includes('Edge/');
		const edgeChromium = this.runtimeIsInBrowser && navigator.userAgent.includes('Edg/');
		const edgeAndroid = this.runtimeIsInBrowser && navigator.userAgent.includes('EdgA/');
		const edgeIos = this.runtimeIsInBrowser && navigator.userAgent.includes('EdgiOS/');
		const edge = edgeHTML || edgeChromium || edgeAndroid || edgeIos;
		const samsungBrowser = this.runtimeIsInBrowser && navigator.userAgent.includes('SamsungBrowser/');
		const opera = this.runtimeIsInBrowser && (navigator.userAgent.includes('Opera') || navigator.userAgent.includes('OPR/'));
		const operaIos = this.runtimeIsInBrowser && navigator.userAgent.includes('OPiOS/');
		const firefoxIos = this.runtimeIsInBrowser && navigator.userAgent.includes('FxiOS/');
		const firefox = this.runtimeIsInBrowser && (navigator.userAgent.includes('Firefox') || firefoxIos);
		const chromeIos = this.runtimeIsInBrowser && navigator.userAgent.includes('CriOS/');
		const chrome =
			this.runtimeIsInBrowser && (navigator.userAgent.includes('Chrome') || chromeIos) && !edge && !opera && !samsungBrowser;
		const safari =
			(this.runtimeIsInBrowser &&
				navigator.userAgent.includes('Safari') &&
				!chrome &&
				!edgeChromium &&
				!firefox &&
				!opera &&
				!samsungBrowser) ||
			edgeIos ||
			chromeIos ||
			firefoxIos;
		const strictSafari =
			this.runtimeIsInBrowser && navigator.userAgent.includes('Safari') && !chrome && !edge && !firefox && !opera && !samsungBrowser;
		const ie = this.runtimeIsInBrowser && navigator.userAgent.includes('Trident');

		const browsers = Object.freeze({
			edgeChromium,
			edgeAndroid,
			edgeIos,
			edge,
			firefoxIos,
			chromeIos,
			samsungBrowser,
			opera,
			operaIos,
			firefox,
			chrome,
			safari,
			strictSafari,
			ie,
		} satisfies BrowsersData);

		const ios = this.runtimeIsInBrowser && /iPad|iPhone|iPod/.test(navigator.userAgent);
		const android = this.runtimeIsInBrowser && /Android/.test(navigator.userAgent);
		// Should pass is-mobile tests suites tbh
		const mobile = ios || android;
		const desktop = !mobile;

		const platform = Object.freeze({
			ios,
			android,
			mobile,
			desktop,
		} satisfies PlatformData);

		this.cachedUserAgentData = Object.freeze({
			browsers,
			platform,
		});

		return this.cachedUserAgentData;
	}
}

interface BrowsersData {
	edgeChromium: boolean;
	edgeAndroid: boolean;
	edgeIos: boolean;
	edge: boolean;
	samsungBrowser: boolean;
	opera: boolean;
	operaIos: boolean;
	firefoxIos: boolean;
	firefox: boolean;
	chromeIos: boolean;
	chrome: boolean;
	safari: boolean;
	strictSafari: boolean;
	ie: boolean;
}

export interface PlatformData {
	ios: boolean;
	android: boolean;
	mobile: boolean;
	desktop: boolean;
}

interface UserAgentData {
	browsers: Readonly<BrowsersData>;
	platform: Readonly<PlatformData>;
}

// prettier-ignore
export enum BrowserList {
	None = 0, // 0 - Runtime is in terminal
	Edge = 1 << 0, // 1
	Safari = 1 << 1, // 2
	StrictSafari = 1 << 2, // 4 - All browsers within iOS is using Safari in the background, but this flag ignores that fact.
	Firefox = 1 << 3, // 8
	Chrome = 1 << 4, // 16
	SamsungBrowser = 1 << 5, // 32
	Opera = 1 << 6, // 64
	IE = 1 << 7, // 128
	EdgeChromium = 1 << 8, // 256
	EdgeAndroid = 1 << 9, // 512
	EdgeIos = 1 << 10, // 1024
	FirefoxIos = 1 << 11, // 2048
	ChromeIos = 1 << 12, // 4096
	OperaIos = 1 << 13, // 8192
}

// prettier-ignore
export enum PlatformList {
	None = 0, // 0 - Runtime is in terminal
	Ios = 1 << 0, // 1
	Android = 1 << 1, // 2
	Mobile = 1 << 2, // 4
	Desktop = 1 << 3, // 8
}
